import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { AvailableLocationInfo, LocationInfoType } from 'record-it-utils/location/GeolocationManager/GeolocationManager';
import { useLocationInfo } from '../../hooks/useLocationInfo/useLocationInfo';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import styles from './LocationContentContainer.module.css';
export type LocationContentContainerProps = Omit<ComponentProps<'div'>, 'children'> & {
  children: (options: {
    locationInfo: AvailableLocationInfo;
  }) => React.ReactNode;
};

/**
 * A container which only renders the content if a location is available.
 * If no location is available, an error message will be shown.
 */
export const LocationContentContainer: FunctionComponent<LocationContentContainerProps> = React.memo(props => {
  const $ = _c(18);
  const {
    children,
    className,
    ...remainingProps
  } = prepareProps({
    props
  });
  const locationInfo = useLocationInfo();
  const {
    t
  } = useTranslation("record-it-components");
  const t0 = classes(className, styles.container);
  const t1 = locationInfo.type === LocationInfoType.Available ? children({
    locationInfo
  }) : null;
  let t2;
  if ($[0] !== locationInfo.type) {
    t2 = locationInfo.type === LocationInfoType.Pending ? <div className={styles.noContentPlaceholder} /> : undefined;
    $[0] = locationInfo.type;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== locationInfo.type || $[3] !== t) {
    t3 = locationInfo.type === LocationInfoType.NoPermission ? <MessageBox type={MessageBoxType.Error} data-testid="LocationContentContainer.MessageBox">{t("location.LocationContentContainer.noPermissionMessage")}</MessageBox> : undefined;
    $[2] = locationInfo.type;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== locationInfo.type || $[6] !== t) {
    t4 = locationInfo.type === LocationInfoType.Unavailable ? <MessageBox type={MessageBoxType.Warning} data-testid="LocationContentContainer.MessageBox">{t("location.LocationContentContainer.unavailableMessage")}</MessageBox> : undefined;
    $[5] = locationInfo.type;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = locationInfo.type === LocationInfoType.Pending;
  let t6;
  if ($[8] !== t5) {
    t6 = <LoadingOverlay active={t5} data-testid="LocationContentContainer.LoadingOverlay" />;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== remainingProps || $[11] !== t0 || $[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t6) {
    t7 = <div {...remainingProps} className={t0}>{t1}{t2}{t3}{t4}{t6}</div>;
    $[10] = remainingProps;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
});